/**
 * Pure helpers for the POSH scenario player and quiz (2026-10-25 POSH
 * review, section C). Plain .ts (no React) so `npm run test:unit` can load
 * it: reading-time reveal, resume position, the scene step in the URL,
 * per-attempt quiz shuffling, the quiz result review and localized lists.
 */

import { localize } from "@/lib/api/posh";
import type {
  GradedAnswer,
  LocalizedText,
  ProgressStatus,
  SafeQuizQuestion,
  SceneContent,
  SubmitQuizResponse,
} from "@/types/posh";
import type { PoshLang } from "./posh-ui-text";

// ---------------------------------------------------------------------------
// Reading time
// ---------------------------------------------------------------------------

/** Average adult silent-reading speed (words per minute). */
export const READING_WPM = 200;
export const MIN_REVEAL_MS = 1500;
export const MAX_REVEAL_MS = 12_000;

export function wordCount(text: string): number {
  const t = text.trim();
  return t ? t.split(/\s+/).length : 0;
}

/** How long before Next appears: reading time at ~200 wpm, 1.5–12 s. */
export function revealDelayMs(words: number): number {
  const ms = Math.round((Math.max(0, words) / READING_WPM) * 60_000);
  return Math.min(MAX_REVEAL_MS, Math.max(MIN_REVEAL_MS, ms));
}

/** Pause before the NEXT dialogue line appears: the reading time of this
 *  line, 0.6–4 s (the whole scene stays within the Next reveal cap). */
export function lineDelayMs(text: string): number {
  const ms = Math.round((wordCount(text) / READING_WPM) * 60_000);
  return Math.min(4000, Math.max(600, ms));
}

/** Every piece of text a scene shows, in the chosen language. */
export function sceneText(content: SceneContent | undefined, lang: PoshLang): string {
  if (!content) return "";
  const parts: string[] = [];
  const add = (v: LocalizedText | undefined | null) => {
    const s = localize(v, lang);
    if (s) parts.push(s);
  };
  add(content.title);
  add(content.text);
  for (const p of content.points ?? []) add(p);
  for (const d of content.dialogues ?? []) add(d.text);
  add(content.callout?.text);
  add(content.situation?.text);
  for (const c of content.choices ?? []) add(c.text);
  for (const p of content.panels ?? []) {
    add(p.heading);
    add(p.description);
  }
  for (const it of content.items ?? []) add(it.text);
  return parts.join(" ");
}

// ---------------------------------------------------------------------------
// Resume position + scene step in the URL
// ---------------------------------------------------------------------------

export interface TimelinePoint {
  scenarioId: number;
  sceneNumber: number;
}

export interface ResumeProgress {
  status?: ProgressStatus;
  current_scenario_id?: number | null;
  current_scene_number?: number | null;
  scenarios_completed?: number[] | null;
}

/**
 * The 0-based timeline index to open the player at. A completed or lapsed
 * module (review / refresher), or one whose scenarios are all done, opens
 * at the start. Otherwise the saved scenario + scene; a saved scene that
 * no longer exists opens at that scenario's first scene; no pointer opens
 * at the first scenario not yet completed.
 */
export function resumeIndex(
  timeline: readonly TimelinePoint[],
  progress: ResumeProgress | undefined | null,
): number {
  if (timeline.length === 0 || !progress) return 0;
  if (progress.status === "completed" || progress.status === "expired") return 0;
  const done = new Set(progress.scenarios_completed ?? []);
  const scenarioIds = [...new Set(timeline.map((t) => t.scenarioId))];
  if (scenarioIds.length > 0 && scenarioIds.every((id) => done.has(id))) return 0;

  const sid = progress.current_scenario_id;
  if (sid != null) {
    const exact = timeline.findIndex(
      (t) => t.scenarioId === sid && t.sceneNumber === progress.current_scene_number,
    );
    if (exact >= 0) return exact;
    const first = timeline.findIndex((t) => t.scenarioId === sid);
    if (first >= 0) return first;
  }
  const firstOpen = timeline.findIndex((t) => !done.has(t.scenarioId));
  return firstOpen >= 0 ? firstOpen : 0;
}

/** `?step=` → a 1-based step, or undefined when absent / not a positive integer. */
export function parseStep(raw: unknown): number | undefined {
  const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
  if (!Number.isInteger(n) || n < 1 || n > 10_000) return undefined;
  return n;
}

/** The 0-based position for a 1-based URL step (or the resume index). */
export function positionFor(step: number | undefined, resume: number, total: number): number {
  if (total <= 0) return 0;
  const p = step !== undefined ? step - 1 : resume;
  return Math.min(total - 1, Math.max(0, p));
}

/** Seconds between two instants, capped (an idle tab must not inflate time). */
export function elapsedSeconds(fromMs: number, toMs: number, capSeconds = 1800): number {
  const s = Math.round((toMs - fromMs) / 1000);
  return Math.min(capSeconds, Math.max(0, s));
}

// ---------------------------------------------------------------------------
// Quiz gate
// ---------------------------------------------------------------------------

/** The quiz opens once every scenario is done (or the module is already
 *  completed). A module with no scenarios opens straight to the quiz. */
export function quizUnlocked(
  status: ProgressStatus | undefined,
  completedCount: number,
  scenarioTotal: number,
): boolean {
  if (status === "completed") return true;
  if (scenarioTotal <= 0) return true;
  return completedCount >= scenarioTotal;
}

// ---------------------------------------------------------------------------
// Quiz shuffling (per attempt, stable across reloads via a stored seed)
// ---------------------------------------------------------------------------

/** mulberry32 — a tiny deterministic PRNG. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const out = [...items];
  const next = rng(seed);
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function newQuizSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff) + 1;
}

/**
 * Questions and their options in a per-attempt order. Ids are untouched
 * (grading is by id). True/false keeps its natural order.
 */
export function shuffleQuiz(
  questions: readonly SafeQuizQuestion[],
  seed: number,
): SafeQuizQuestion[] {
  const base = [...questions].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  return seededShuffle(base, seed).map((q) =>
    q.question_type === "true_false"
      ? q
      : { ...q, options: seededShuffle(q.options, (seed ^ Math.imul(q.id, 2654435761)) >>> 0) },
  );
}

/** Answers kept for this attempt (sessionStorage) — validated, never trusted. */
export interface StoredQuiz {
  seed: number;
  answers: Record<number, string[]>;
}

// The key includes the user id (shared devices) — see lib/posh-quiz-storage.
export { quizStorageKey } from "@/lib/posh-quiz-storage";

export function parseStoredQuiz(raw: string | null): StoredQuiz | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== "object") return null;
    const { seed, answers } = v as { seed?: unknown; answers?: unknown };
    if (typeof seed !== "number" || !Number.isFinite(seed)) return null;
    const out: Record<number, string[]> = {};
    if (answers && typeof answers === "object") {
      for (const [k, sel] of Object.entries(answers as Record<string, unknown>)) {
        const id = Number(k);
        if (!Number.isInteger(id) || !Array.isArray(sel)) continue;
        out[id] = sel.filter((s): s is string => typeof s === "string");
      }
    }
    return { seed, answers: out };
  } catch {
    return null;
  }
}

/** The selected option ids for a question that still exist on it. */
export function selectedFor(q: SafeQuizQuestion, answers: Record<number, string[]>): string[] {
  const ids = new Set(q.options.map((o) => o.id));
  return (answers[q.id] ?? []).filter((id) => ids.has(id));
}

// ---------------------------------------------------------------------------
// Quiz result review
// ---------------------------------------------------------------------------

export interface QuizReviewItem {
  questionId: number;
  /** 1-based number as shown on the quiz. */
  number: number;
  question: LocalizedText;
  isCorrect: boolean;
  selected: string[];
  /** Absent when the server sent none (older backend). */
  explanation?: LocalizedText;
  /** Correct option ids — only when the server reveals answers. */
  correctOptionIds?: string[];
}

function asIdList(v: unknown): string[] | undefined {
  if (Array.isArray(v)) {
    const ids = v.filter((x): x is string => typeof x === "string");
    return ids.length ? ids : undefined;
  }
  if (typeof v === "string" && v) return [v];
  return undefined;
}

function hasText(v: LocalizedText | undefined | null): boolean {
  return localize(v, "en") !== "";
}

/**
 * One row per question in the order the learner saw them. The correct
 * option is listed ONLY when the response says `reveal_answers` (the
 * server decides when answers may be shown). Returns [] when the response
 * carries no per-question data.
 */
export function buildQuizReview(
  questions: readonly SafeQuizQuestion[],
  response: Pick<SubmitQuizResponse, "answers"> & { reveal_answers?: boolean },
): QuizReviewItem[] {
  const graded = new Map<number, GradedAnswer>();
  for (const a of response.answers ?? []) graded.set(a.question_id, a);
  if (graded.size === 0) return [];
  const reveal = response.reveal_answers === true;
  return questions.map((q, i) => {
    const g = graded.get(q.id);
    const correct = reveal ? asIdList(g?.correct_answer) : undefined;
    return {
      questionId: q.id,
      number: i + 1,
      question: q.question_text,
      isCorrect: g?.is_correct === true,
      selected: g?.selected ?? [],
      explanation: g && hasText(g.explanation) ? g.explanation : undefined,
      correctOptionIds: correct,
    };
  });
}

/** Option ids → their text in the chosen language, joined. */
export function optionTexts(
  q: SafeQuizQuestion | undefined,
  ids: readonly string[],
  lang: PoshLang,
): string {
  if (!q) return "";
  return ids
    .map((id) => q.options.find((o) => o.id === id))
    .filter((o): o is NonNullable<typeof o> => o !== undefined)
    .map((o) => localize(o.text, lang))
    .filter(Boolean)
    .join("; ");
}

// ---------------------------------------------------------------------------
// Localized lists (module objectives)
// ---------------------------------------------------------------------------

/**
 * A localized list in either shape — `[{en,hi}, …]` or `{en:[…], hi:[…]}` —
 * or a single string. Falls back to English, then to any language.
 */
export function localizedList(value: unknown, lang: PoshLang): string[] {
  if (value == null) return [];
  if (typeof value === "string") return value.trim() ? [value] : [];
  if (Array.isArray(value)) {
    return value
      .map((v) => localize(v as LocalizedText, lang))
      .filter((s) => s.trim() !== "");
  }
  if (typeof value === "object") {
    const bag = value as Record<string, unknown>;
    const pick = (k: string) =>
      Array.isArray(bag[k])
        ? (bag[k] as unknown[]).filter((s): s is string => typeof s === "string" && s.trim() !== "")
        : [];
    const own = pick(lang);
    if (own.length) return own;
    const en = pick("en");
    if (en.length) return en;
    for (const k of Object.keys(bag)) {
      const any = pick(k);
      if (any.length) return any;
    }
    const single = localize(bag as LocalizedText, lang);
    return single ? [single] : [];
  }
  return [];
}
